import { useState } from "react";
import api from "@/lib/api";

const SIZES = {
  xs: "h-5 w-5 text-[8px]",
  sm: "h-6 w-6 text-[9px]",
  md: "h-8 w-8 text-[10px]",
  lg: "h-11 w-11 text-xs",
};

/**
 * Logo d'une association (rond). Affiche le logo officiel si disponible,
 * sinon une pastille de secours avec l'acronyme sur la couleur de l'asso.
 * `assoc` : { association_id, acronym, color, has_logo, name, count? }
 */
export function AssocLogo({ assoc, size = "md", ring = false, title, className = "" }) {
  const [err, setErr] = useState(false);
  const dim = SIZES[size] || SIZES.md;
  const showImg = assoc?.has_logo && !err && assoc?.association_id;
  const ringCls = ring ? "ring-2 ring-[var(--shariti-orange)] ring-offset-1 ring-offset-white" : "";
  const label = title || assoc?.name || assoc?.acronym || "Association";
  return (
    <div
      className={`grid ${dim} shrink-0 place-items-center overflow-hidden rounded-full border border-[var(--border-soft)] ${showImg ? "bg-white" : ""} ${ringCls} ${className}`}
      style={showImg ? undefined : { background: assoc?.color || "#EE722E" }}
      title={label}
      aria-label={label}
      data-testid={`assoc-logo-${assoc?.association_id || assoc?.acronym}`}
    >
      {showImg ? (
        <img
          src={`${api.defaults.baseURL}/assets/association-logo/${assoc.association_id}`}
          alt={assoc.acronym}
          className="h-full w-full object-contain p-0.5"
          onError={() => setErr(true)}
          loading="lazy"
        />
      ) : (
        <span className="font-extrabold uppercase leading-none text-white">
          {(assoc?.acronym || "?").slice(0, 3)}
        </span>
      )}
    </div>
  );
}

/**
 * Pile de logos d'associations qui se chevauchent (aperçu compact en liste).
 * L'association "habituée" (is_habitue) est mise en avant par un liseré orange.
 * `items` : liste de récapitulatifs compacts (voir AssocLogo).
 */
export function AssocLogoStack({ items = [], max = 4, size = "sm", testId }) {
  if (!items || items.length === 0) {
    return <span className="text-[10px] text-muted-ink/50">—</span>;
  }
  const shown = items.slice(0, max);
  const rest = items.length - shown.length;
  return (
    <div className="flex items-center" data-testid={testId}>
      {shown.map((it, i) => (
        <div key={it.association_id || it.acronym || i} className={i > 0 ? "-ml-1.5" : ""} style={{ zIndex: shown.length - i }}>
          <AssocLogo
            assoc={it}
            size={size}
            ring={!!it.is_habitue}
            title={`${it.name || it.acronym}${it.count ? ` — ${it.count} passage${it.count > 1 ? "s" : ""}` : ""}${it.is_habitue ? " (asso habituée)" : ""}`}
          />
        </div>
      ))}
      {rest > 0 && (
        <span className="ml-1 rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[9px] font-bold text-muted-ink" title={`+${rest} autre${rest > 1 ? "s" : ""} association${rest > 1 ? "s" : ""}`}>
          +{rest}
        </span>
      )}
    </div>
  );
}
